import Box from '@mui/material/Box';
import MenuIcon from '@mui/icons-material/Menu';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import ListItemIcon from '@mui/material/ListItemIcon';
import Avatar from '@mui/material/Avatar';
import LogoutIcon from '@mui/icons-material/Logout';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import KeyIcon from '@mui/icons-material/Key';
import GridViewIcon from '@mui/icons-material/GridView';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import BarChartIcon from '@mui/icons-material/BarChart';
import PeopleIcon from '@mui/icons-material/People';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import DescriptionIcon from '@mui/icons-material/Description';
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism';
import ReceiptIcon from '@mui/icons-material/Receipt';
import type { SvgIconComponent } from '@mui/icons-material';
import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/auth';
import { APP_BASE } from '@/lib/api-base';
import { EntitySelector } from './EntitySelector';
import { ChangePasswordDialog } from './ChangePasswordDialog';
import { ConfirmDialog } from './ConfirmDialog';
import { NotificationBell } from './NotificationBell';
import { useSidebarStore } from '@/store/sidebar';
import { useEntityStore } from '@/store/entity';
import { useActiveBranding } from '@/api/settings';

type Props = { height: number; showMenuButton?: boolean; onMenuClick?: () => void };

type QuickLink = { to: string; label: string; icon: SvgIconComponent };

/** Quick-access shortcuts in the topbar — the handful of modules an operator
 *  jumps to constantly, so they're one click away from any page. Role-aware. */
const ADMIN_QUICK: QuickLink[] = [
  { to: '',              label: 'Dashboard',    icon: GridViewIcon },
  { to: '/payroll-runs', label: 'Payroll Runs', icon: RocketLaunchIcon },
  { to: '/approvals',    label: 'Approvals',    icon: FactCheckIcon },
  { to: '/employees',    label: 'Employees',    icon: PeopleIcon },
  { to: '/reports',      label: 'Reports',      icon: BarChartIcon },
];
const ESS_QUICK: QuickLink[] = [
  { to: '/me',              label: 'Dashboard',      icon: GridViewIcon },
  { to: '/me/payslips',     label: 'My Payslips',    icon: ReceiptLongIcon },
  { to: '/me/declarations', label: 'IT Declarations', icon: DescriptionIcon },
  { to: '/me/flexi',        label: 'Flexi Benefits', icon: VolunteerActivismIcon },
  { to: '/me/reimbursements', label: 'Reimbursements', icon: ReceiptIcon },
];

/**
 * Topbar — entity selector + user menu.
 *
 * User menu actions:
 *   • Change password   → opens dialog (calls /auth/change-password)
 *   • Sign out          → confirm prompt, clears auth store + react-query cache,
 *                         redirects to /login. Confirm prevents accidental misclicks
 *                         after a long workflow (e.g. half-edited declaration).
 */
export function Topbar({ height, showMenuButton = false, onMenuClick }: Props) {
  const user = useAuthStore((s) => s.user);
  const clear = useAuthStore((s) => s.clear);
  const navigate = useNavigate();
  const location = useLocation();
  const qc = useQueryClient();

  const quickLinks = user?.role === 'EMPLOYEE' ? ESS_QUICK : ADMIN_QUICK;
  const sidebarCollapsed = useSidebarStore((s) => s.collapsed);
  const toggleSidebar = useSidebarStore((s) => s.toggle);

  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [pwOpen, setPwOpen]   = useState(false);
  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);

  const onLogout = () => {
    setAnchor(null);
    setLogoutConfirmOpen(false);
    // Drop every server-fetched query so the next sign-in starts with a clean slate.
    qc.clear();
    clear();
    navigate(`${APP_BASE}/login`);
  };

  const initials = ((user?.full_name || user?.username || '?')
    .split(/\s+/).filter(Boolean).slice(0, 2).map((s) => s[0]).join('') || '?')
    .toUpperCase();

  const entityId = useEntityStore((s) => s.entityId);
  const headerBg = useActiveBranding(entityId)?.header_color || '#ffffff';

  return (
    <Box
      component="header"
      sx={{
        height,
        flexShrink: 0,
        px: 2.5,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        bgcolor: headerBg,
        borderBottom: '1px solid #e6ebf0',
        // Slight shadow separating the header band from the content below.
        boxShadow: '0 2px 8px rgba(24,40,72,0.06)',
        position: 'relative', zIndex: 2,
      }}
    >
      {showMenuButton && (
        <IconButton onClick={onMenuClick} aria-label="Open menu" edge="start" sx={{ mr: 0.5 }}>
          <MenuIcon />
        </IconButton>
      )}

      {/* Desktop sidebar collapse toggle (mobile uses the hamburger above). */}
      {!showMenuButton && (
        <Tooltip title={sidebarCollapsed ? 'Expand menu' : 'Collapse menu'} arrow>
          <IconButton onClick={toggleSidebar} aria-label="Toggle sidebar"
            sx={{ mr: 0.5, color: '#5c6773', '&:hover': { color: 'primary.main' } }}>
            <MenuOpenIcon sx={{ transform: sidebarCollapsed ? 'rotate(180deg)' : 'none', transition: 'transform 200ms' }} />
          </IconButton>
        </Tooltip>
      )}

      {/* Quick-access module shortcuts (uKnowva-style icon rail). */}
      <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 0.25 }}>
        {quickLinks.map((q) => {
          const to = `${APP_BASE}${q.to}`;
          const isHome = q.to === '' || q.to === '/me';
          const active = isHome
            ? location.pathname === to
            : location.pathname === to || location.pathname.startsWith(`${to}/`);
          const Ico = q.icon;
          return (
            <Tooltip key={q.to} title={q.label} arrow>
              <IconButton
                onClick={() => navigate(to)}
                aria-label={q.label}
                sx={{
                  width: 38, height: 38, borderRadius: 2,
                  color: active ? 'primary.main' : '#6b7683',
                  bgcolor: active ? 'primary.light' : 'transparent',
                  transition: 'background-color 140ms, color 140ms',
                  '&:hover': { bgcolor: 'primary.light', color: 'primary.main' },
                }}
              >
                <Ico sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
          );
        })}
        <Divider orientation="vertical" flexItem sx={{ mx: 0.75, my: 1.25 }} />
      </Box>

      <Box sx={{ flex: 1 }} />

      <EntitySelector />

      <NotificationBell />

      {/* Identity cluster — wireframe shows avatar + name + role, not a bare
          avatar, so the signed-in user is readable without opening the menu. */}
      <Box
        onClick={(e) => setAnchor(e.currentTarget as HTMLElement)}
        role="button"
        tabIndex={0}
        aria-label="user menu"
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setAnchor(e.currentTarget as HTMLElement);
          }
        }}
        sx={{
          ml: 0.5, display: 'flex', alignItems: 'center', gap: 1,
          px: 0.75, py: 0.5, borderRadius: 1, cursor: 'pointer',
          transition: 'background 150ms',
          '&:hover': { background: '#f3f3f9' },
        }}
      >
        <Avatar sx={{
          width: 32, height: 32,
          bgcolor: 'primary.main',
          fontSize: 12.5,
          fontWeight: 700,
        }}>
          {initials}
        </Avatar>
        <Box sx={{
          display: { xs: 'none', md: 'flex' },
          alignItems: 'baseline', gap: 0.75, minWidth: 0,
        }}>
          <Typography sx={{
            fontSize: 13.5, fontWeight: 700, color: 'text.primary',
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            maxWidth: 180,
          }}>
            {user?.full_name || user?.username || 'Unknown'}
          </Typography>
          <Typography sx={{
            fontSize: 11.5, color: 'text.secondary', whiteSpace: 'nowrap',
          }}>
            {user?.role}
          </Typography>
        </Box>
        <ExpandMoreIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
      </Box>

      <Menu
        anchorEl={anchor}
        open={!!anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: {
          minWidth: 268, mt: 1, borderRadius: 2.5, overflow: 'visible',
          boxShadow: '0 10px 30px rgba(24,40,72,0.16)',
          border: '1px solid #eceff3',
          '& .MuiList-root': { py: 0.75 },
        } } }}
      >
        {/* Identity header */}
        <Box sx={{ px: 2, py: 1.5, display: 'flex', gap: 1.25, alignItems: 'center' }}>
          <Avatar sx={{ width: 42, height: 42, bgcolor: 'primary.main', fontWeight: 700, fontSize: 15 }}>
            {initials}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 700, fontSize: 14, lineHeight: 1.25, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {user?.full_name || user?.username || 'Unknown'}
            </Typography>
            <Typography sx={{ fontSize: 11.5, color: 'text.secondary', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {user?.email || user?.username}
            </Typography>
            <Box component="span" sx={{
              display: 'inline-block', mt: 0.5, px: 0.75, py: '1px',
              fontSize: 9.5, fontWeight: 700, letterSpacing: '0.05em',
              color: 'primary.main', bgcolor: 'primary.light', borderRadius: 1,
            }}>
              {user?.role}
            </Box>
          </Box>
        </Box>
        <Divider sx={{ mb: 0.5 }} />
        <MenuItem
          onClick={() => { setAnchor(null); setPwOpen(true); }}
          sx={{ mx: 1, borderRadius: 1.5, py: 0.9 }}
        >
          <ListItemIcon sx={{ minWidth: 32 }}><KeyIcon fontSize="small" /></ListItemIcon>
          Change password
        </MenuItem>
        <MenuItem
          onClick={() => { setAnchor(null); setLogoutConfirmOpen(true); }}
          sx={{ mx: 1, borderRadius: 1.5, py: 0.9, color: 'error.main', '&:hover': { bgcolor: '#fdecea' } }}
        >
          <ListItemIcon sx={{ minWidth: 32 }}><LogoutIcon fontSize="small" sx={{ color: 'error.main' }} /></ListItemIcon>
          Sign out
        </MenuItem>
      </Menu>

      <ChangePasswordDialog open={pwOpen} onClose={() => setPwOpen(false)} />

      <ConfirmDialog
        open={logoutConfirmOpen}
        title="Sign out?"
        confirmLabel="Sign out"
        onCancel={() => setLogoutConfirmOpen(false)}
        onConfirm={onLogout}
        description={
          <Box>
            You'll be signed out of this browser. Any unsaved edits will be lost.
          </Box>
        }
      />
    </Box>
  );
}
